import React, { useMemo, useState } from 'react';
import { type DocumentElement as DocumentElementType } from '@eaco/forms-core';
import {
  FieldHeader,
  FieldErrors,
  FieldHelpText,
  FIELD_SHELL_CLASS,
  FIELD_BARE_INPUT_CLASS,
} from './FieldParts';
import { cn } from '../lib/utils';
import {
  useElementValue,
  useIsEditor,
  useIsReadOnly,
} from '../DocumentView/DocumentContext';
import HiddenElementBanner from './HiddenElementBanner';
import ArchivedElementBanner from './ArchivedElementBanner';
import { useDefaultValue } from './useDefaultValue';

interface Props {
  element: DocumentElementType;
}

/** Coerce a configForm-stored numeric (always a string) to a finite number or undefined. */
const parseFiniteNumber = (v: unknown): number | undefined => {
  if (v === '' || v === null || v === undefined) return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};

/** Coerce to a non-negative integer or undefined (for decimalPlaces). */
// Capped at Intl's 20 fraction digits so a bad setting cannot throw.
const parseNonNegativeInt = (v: unknown): number | undefined => {
  if (v === '' || v === null || v === undefined) return undefined;
  const n = Number(v);
  if (!Number.isFinite(n) || n < 0) return undefined;
  return Math.min(Math.floor(n), 20);
};

/** Strip everything that isn't a digit, a single dot or a leading minus. */
const sanitiseAmount = (raw: string): string => {
  let out = raw.replace(/[^0-9.\-]/g, '');
  // Keep a leading minus only.
  const negative = out.startsWith('-');
  out = out.replace(/-/g, '');
  // Collapse to a single decimal point.
  const firstDot = out.indexOf('.');
  if (firstDot !== -1) {
    out =
      out.slice(0, firstDot + 1) + out.slice(firstDot + 1).replace(/\./g, '');
  }
  return (negative ? '-' : '') + out;
};

/** Format a canonical numeric string with thousands separators + fixed decimals. */
const formatAmount = (raw: string | undefined, decimals: number): string => {
  if (raw === undefined || raw === '' || raw === '-') return '';
  const n = Number(raw);
  if (!Number.isFinite(n)) return '';
  return n.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

/**
 * Currency input (Elements Library): an accent-tinted symbol + code prefix
 * cell (e.g. `$ AUD`) sits left of the amount field, which formats to the
 * configured decimal places with thousands separators when not being edited.
 * The currency symbol/code are set by the designer (fixed per field), not
 * filler-changeable, so the prefix is static at fill time.
 */
const CurrencyInput: React.FC<Props> = ({ element }) => {
  const [value, setValue] = useElementValue<string>(element.id);
  const isEditor = useIsEditor();
  const isReadOnly = useIsReadOnly();
  const [touched, setTouched] = useState(false);
  const [focused, setFocused] = useState(false);
  const data = element.data as {
    label?: string;
    subLabel?: string;
    placeholder?: string;
    required?: boolean;
    helpText?: string;
    hidden?: boolean;
    currencySymbol?: string;
    currencyCode?: string;
    decimalPlaces?: number | string;
    min?: number | string;
    max?: number | string;
    defaultValue?: number | string;
  };

  const label = data?.label || 'Amount';
  const subLabel = data?.subLabel;
  const required = data?.required || false;
  const helpText = data?.helpText;
  const symbol = data?.currencySymbol || '$';
  const code = data?.currencyCode || 'AUD';
  const decimals = parseNonNegativeInt(data?.decimalPlaces) ?? 2;
  const placeholder =
    data?.placeholder || (decimals > 0 ? `0.${'0'.repeat(decimals)}` : '0');
  const configuredMin = parseFiniteNumber(data?.min);
  const configuredMax = parseFiniteNumber(data?.max);

  // Hydrate a configured default (stored as a canonical numeric string).
  const defaultRaw =
    data?.defaultValue === undefined || data?.defaultValue === null
      ? undefined
      : String(data.defaultValue);
  const defaultForHydration =
    defaultRaw !== undefined &&
    defaultRaw !== '' &&
    Number.isFinite(Number(defaultRaw))
      ? defaultRaw
      : undefined;
  const displayValue = useDefaultValue(value, setValue, defaultForHydration);

  // While editing the filler sees the raw canonical value; otherwise the
  // formatted amount (thousands separators + fixed decimals).
  const shown = focused
    ? displayValue ?? ''
    : formatAmount(displayValue, decimals);

  const errors = useMemo<string[]>(() => {
    if (isEditor || !touched) return [];
    const out: string[] = [];
    const raw = String(value ?? '').trim();
    if (required && raw === '') out.push('This field is required');
    if (raw !== '' && raw !== '-') {
      const num = Number(raw);
      if (!Number.isFinite(num)) {
        out.push('Must be a number');
      } else {
        if (configuredMin !== undefined && num < configuredMin) {
          out.push(`Must be ${formatAmount(String(configuredMin), decimals)} or greater`);
        }
        if (configuredMax !== undefined && num > configuredMax) {
          out.push(`Must be ${formatAmount(String(configuredMax), decimals)} or less`);
        }
      }
    }
    return Array.from(new Set(out));
  }, [isEditor, touched, required, value, configuredMin, configuredMax, decimals]);

  const Prefix = (
    <span
      className="flex flex-none items-center gap-[6px] px-[12px] py-[var(--forms-input-py)]"
      style={{
        backgroundColor:
          'color-mix(in srgb, var(--forms-accent, var(--forms-accent-default)) 9%, #fff)',
        borderRight:
          '1px solid color-mix(in srgb, var(--forms-accent, var(--forms-accent-default)) 18%, #fff)',
      }}
    >
      <span className="text-[14px] font-extrabold text-[var(--forms-accent,var(--forms-accent-default))]">
        {symbol}
      </span>
      {code && (
        <span className="text-[12.5px] font-bold text-[var(--forms-muted)]">
          {code}
        </span>
      )}
    </span>
  );

  // Read-only (e.g. a completed result): static formatted amount.
  if (isReadOnly) {
    const formatted = formatAmount(displayValue, decimals);
    return (
      <div className="space-y-2">
        <FieldHeader
          htmlFor={element.id}
          label={label}
          subLabel={subLabel}
          required={required}
        />
        <p className="text-sm">
          {formatted ? (
            <>
              <span className="font-semibold text-[var(--forms-accent,var(--forms-accent-default))]">
                {symbol}
              </span>
              {formatted}
              {code ? ` ${code}` : ''}
            </>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </p>
        <FieldHelpText>{helpText}</FieldHelpText>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <FieldHeader
        htmlFor={element.id}
        label={label}
        subLabel={subLabel}
        required={required}
      />
      <div className={cn(FIELD_SHELL_CLASS, 'items-stretch')}>
        {Prefix}
        <input
          id={element.id}
          type="text"
          inputMode="decimal"
          placeholder={placeholder}
          value={shown}
          onFocus={() => setFocused(true)}
          onChange={(e) => {
            setTouched(true);
            setValue(sanitiseAmount(e.target.value));
          }}
          onBlur={() => {
            setFocused(false);
            setTouched(true);
          }}
          className={cn(FIELD_BARE_INPUT_CLASS, 'px-[var(--forms-input-px)]')}
        />
      </div>
      <FieldErrors errors={errors} />
      <FieldHelpText>{helpText}</FieldHelpText>
      <HiddenElementBanner hidden={data?.hidden} />
      <ArchivedElementBanner archived={element.archived} />
    </div>
  );
};

export default CurrencyInput;
